import DocumentFieldGrid from './DocumentFieldGrid'
import DocumentSection from './DocumentSection'
import DocumentStatusBadge from './DocumentStatusBadge'
import SignatureBlock from './SignatureBlock'
import VehicleDocumentTable from './VehicleDocumentTable'
import { DOCUMENT_FALLBACKS } from './documentFallbacks'
import { formatAddress, formatDate, formatMoney, formatPhone, safeText, safeVin, shortId } from './documentFormatters'

interface DispatchSheetViewProps {
  load: any
  job?: any
  invoice?: any
}

export default function DispatchSheetView({ load, job, invoice }: DispatchSheetViewProps) {
  const loadRow = load ?? {}
  const jobRow = job ?? {}
  const invoiceRow = invoice ?? null
  const vehicles = Array.isArray(loadRow.vehicles) ? loadRow.vehicles : []
  const carrierPay = Number(
    (typeof loadRow.carrierPayCents === 'number' ? loadRow.carrierPayCents / 100 : undefined)
      ?? loadRow.carrierPay
      ?? loadRow.totalPayment
      ?? 0
  )
  const codAmount = Number(jobRow.codAmount ?? loadRow.codAmount ?? 0)
  const balanceAfterCod = carrierPay > 0 ? Math.max(carrierPay - codAmount, 0) : null

  return (
    <div className="space-y-4 text-slate-900">
      <DocumentSection title="Header" subtitle="Operational dispatch tender for the carrier and driver.">
        <DocumentFieldGrid
          items={[
            { label: 'Order ID', value: shortId(jobRow.job_number ?? jobRow.jobNumber ?? jobRow.id) },
            { label: 'Load / Shipment', value: shortId(loadRow.id) },
            { label: 'Dispatch Date', value: formatDate(jobRow.assignedAt ?? loadRow.updated_at ?? loadRow.created_at) },
            { label: 'CD / Reference', value: safeText(jobRow.referenceNumber ?? loadRow.referenceNumber ?? jobRow.jobNumber ?? loadRow.loadNumber, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Total Vehicles', value: String(vehicles.length || loadRow.vehicleCount || 0) },
            { label: 'Status', value: <DocumentStatusBadge tone={jobRow.status === 'DELIVERED' ? 'green' : jobRow.status === 'ASSIGNED' ? 'blue' : 'amber'} label={safeText(jobRow.status, 'TBD')} /> },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Carrier Information" subtitle="Who is moving the shipment.">
        <DocumentFieldGrid
          items={[
            { label: 'Carrier Name', value: safeText(loadRow.carrier_name ?? loadRow.carrierName ?? jobRow.carrier_name ?? jobRow.carrierName ?? loadRow.carrier_org_id ?? jobRow.carrier_org_id, DOCUMENT_FALLBACKS.notAssigned) },
            { label: 'Carrier Address', value: formatAddress(loadRow.carrier_address ?? jobRow.carrier_address ?? loadRow.carrierAddress) },
            { label: 'MC Number', value: safeText(loadRow.carrier_mc_number ?? jobRow.carrier_mc_number ?? loadRow.mcNumber ?? jobRow.mcNumber, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Driver Name', value: safeText(jobRow.driver_name ?? jobRow.driverName ?? jobRow.driver_user_id ?? jobRow.driverUserId, DOCUMENT_FALLBACKS.notAssigned) },
            { label: 'Driver Phone', value: formatPhone(jobRow.driver_phone ?? jobRow.driverPhone) },
            { label: 'Dispatch Email', value: safeText(loadRow.dispatch_email ?? jobRow.dispatch_email ?? loadRow.contact_email ?? jobRow.contact_email, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Carrier Contact', value: safeText(loadRow.carrier_contact ?? jobRow.carrier_contact ?? loadRow.contact_name ?? jobRow.contact_name, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Carrier Phone / Fax', value: safeText(loadRow.carrier_phone ?? jobRow.carrier_phone ?? loadRow.carrier_fax ?? jobRow.carrier_fax, DOCUMENT_FALLBACKS.contactDispatcher) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Order Information" subtitle="Commercial terms and operational commitments.">
        <DocumentFieldGrid
          items={[
            { label: 'Pickup Estimated', value: safeText(loadRow.pickupDate ?? loadRow.requestedPickup ?? jobRow.pickupEstimatedAt, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Delivery Estimated', value: safeText(loadRow.deliveryDate ?? loadRow.requestedDelivery ?? jobRow.deliveryEstimatedAt, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Ship Via', value: safeText(loadRow.ship_via ?? loadRow.shipVia ?? 'Open', 'Open') },
            { label: 'Condition', value: safeText(loadRow.condition ?? loadRow.vehicleCondition ?? 'Operable', 'Operable') },
            { label: 'Price Listed', value: formatMoney(loadRow.priceListed ?? loadRow.listedPrice ?? carrierPay) },
            { label: 'Carrier Pay', value: formatMoney(carrierPay) },
            { label: 'COD / On Delivery', value: formatMoney(codAmount) },
            { label: 'Balance After COD', value: balanceAfterCod == null ? DOCUMENT_FALLBACKS.tbd : formatMoney(balanceAfterCod) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Dispatch Company / Broker" subtitle="Diamond Dispatch issuing office.">
        <DocumentFieldGrid
          items={[
            { label: 'Company Name', value: 'Diamond Dispatch' },
            { label: 'Company Address', value: 'AUXuniverse Dispatch Operations' },
            { label: 'Company Phone', value: formatPhone(loadRow.company_phone ?? 'Contact dispatcher') },
            { label: 'Dispatch Contact', value: safeText(jobRow.dispatcher_name ?? 'Dispatch Desk', 'Dispatch Desk') },
            { label: 'Dispatch Phone', value: formatPhone(jobRow.dispatch_phone ?? loadRow.dispatch_phone ?? 'Contact dispatcher') },
            { label: 'MC Number', value: safeText(loadRow.dispatch_mc_number ?? jobRow.dispatch_mc_number ?? 'TBD', 'TBD') },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Vehicle Information" subtitle="Vehicle-specific tender details.">
        <VehicleDocumentTable
          rows={vehicles}
          columns={[
            { label: 'Unit', render: (vehicle, index) => safeText(vehicle.unitNumber ?? vehicle.unit ?? index + 1, `Unit ${index + 1}`) },
            { label: 'Vehicle', render: (vehicle) => [safeText(vehicle.year, ''), safeText(vehicle.make, ''), safeText(vehicle.model, ''), safeText(vehicle.trim, '')].filter(Boolean).join(' ').trim() || DOCUMENT_FALLBACKS.notProvided },
            { label: 'Type / Body', render: (vehicle) => safeText(vehicle.bodyStyle ?? vehicle.type ?? vehicle.body_type, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Color', render: (vehicle) => safeText(vehicle.color, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Plate', render: (vehicle) => safeText(vehicle.plate ?? vehicle.licensePlate ?? vehicle.tag, DOCUMENT_FALLBACKS.tbd) },
            { label: 'VIN', render: (vehicle) => safeVin(vehicle.vin) },
            { label: 'Lot Number', render: (vehicle) => safeText(vehicle.lotNumber ?? vehicle.lot_number, DOCUMENT_FALLBACKS.tbd) },
            { label: 'Additional Info', render: (vehicle) => safeText(vehicle.additionalInfo ?? vehicle.notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          emptyMessage="No vehicle records are available for this order."
        />
      </DocumentSection>

      <DocumentSection title="Pickup Information" subtitle="Who and where to collect the vehicle.">
        <DocumentFieldGrid
          items={[
            { label: 'Pickup Contact', value: safeText(loadRow.pickup_contact ?? loadRow.origin_contact ?? loadRow.contact_name, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Pickup Address', value: formatAddress(loadRow.origin_address ?? loadRow.origin ?? loadRow.pickupAddress) },
            { label: 'Pickup Phone', value: formatPhone(loadRow.pickup_phone ?? loadRow.origin_phone ?? loadRow.contact_phone) },
            { label: 'Pickup Notes', value: safeText(loadRow.pickup_notes ?? loadRow.origin_notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Delivery Information" subtitle="Where the vehicle must be delivered.">
        <DocumentFieldGrid
          items={[
            { label: 'Delivery Contact', value: safeText(loadRow.delivery_contact ?? loadRow.destination_contact ?? loadRow.contact_name, DOCUMENT_FALLBACKS.contactDispatcher) },
            { label: 'Delivery Address', value: formatAddress(loadRow.destination_address ?? loadRow.destination ?? loadRow.deliveryAddress) },
            { label: 'Delivery Phone', value: formatPhone(loadRow.delivery_phone ?? loadRow.destination_phone ?? loadRow.contact_phone) },
            { label: 'Delivery Notes', value: safeText(loadRow.delivery_notes ?? loadRow.destination_notes ?? loadRow.notes, DOCUMENT_FALLBACKS.notProvided) },
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="Dispatch Instructions" subtitle="Diamond Dispatch operating language.">
        <ul className="grid gap-2 text-sm text-slate-900 sm:grid-cols-2">
          <li className="rounded border border-slate-200 px-3 py-2">Pickup date window must be honored.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Delivery date window must be honored.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Provide 24-hour customer notice before pickup and delivery where possible.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Perform a thorough vehicle inspection before loading and at delivery.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Signed delivery receipt is required for payment processing.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Invoice must include delivery receipt and POD attachments.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Authority to transport is granted only for the shipment described here.</li>
          <li className="rounded border border-slate-200 px-3 py-2">Marketplace / broker disclaimer placeholder applies until counter-signed.</li>
        </ul>
      </DocumentSection>

      <DocumentSection title="Dispatch Acknowledgment">
        <SignatureBlock
          labels={[
            { label: 'Carrier Signature', subtitle: 'Authorized carrier representative' },
            { label: 'Print Name', subtitle: 'Carrier representative' },
            { label: 'Date / Time', subtitle: 'Execution date and time' },
          ]}
        />
      </DocumentSection>
    </div>
  )
}
